import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useCreditGoal, useSetCreditGoal } from '@/hooks/queries/useGoals';

export interface CreditGoalSettingProps {
  goal: number | null;
  isSaving: boolean;
  /** `null` clears the standing goal. */
  onSave: (goal: number | null) => void;
}

export function CreditGoalSettingView({ goal, isSaving, onSave }: CreditGoalSettingProps) {
  const [draft, setDraft] = useState(goal === null ? '' : String(goal));
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    const parsed = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      setError('Enter a non-negative dollar amount, or clear the goal.');
      return;
    }
    setError(null);
    onSave(parsed);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Trading preferences</CardTitle>
        <CardDescription>Targets Steward measures each month against</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label htmlFor="monthly-credit-goal" className="block text-sm font-medium text-gray-700">
          Monthly credit goal
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">$</span>
          <input
            id="monthly-credit-goal"
            aria-label="Monthly credit goal"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="No goal set"
            className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <Button size="sm" aria-label="Save monthly credit goal" disabled={isSaving} onClick={save}>
            Save
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-label="Clear monthly credit goal"
            disabled={isSaving}
            onClick={() => { setDraft(''); setError(null); onSave(null); }}
          >
            Clear
          </Button>
        </div>
        {error && (
          <p data-testid="credit-goal-error" className="text-sm text-red-600">{error}</p>
        )}
        <p className="text-xs text-gray-500">
          Measured on net credit: what the month sold, less what it cost to close and roll. A
          single month can be given its own target
          from the Credit by month table on Analytics.
        </p>
      </CardContent>
    </Card>
  );
}

export function CreditGoalSetting() {
  // One month is enough: only the standing `goal` is read here.
  const { data, isError } = useCreditGoal(1);
  const setGoal = useSetCreditGoal();

  // The view seeds its draft from `goal` ONCE, on mount. Mounting it before the query lands would
  // open an empty box over a stored goal — and Save would then reject the entry as empty. So the
  // card holds a spinner until there is a goal to seed from, keeping its place on the page.
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trading preferences</CardTitle>
          <CardDescription>Targets Steward measures each month against</CardDescription>
        </CardHeader>
        <CardContent>
          {/* A failed read must say so, as the tile and the table do — an endless spinner would
              read as "still loading" forever. */}
          {isError ? (
            <p className="py-8 text-center text-sm text-gray-500" data-testid="credit-goal-error">
              Could not load the monthly goal.
            </p>
          ) : (
            <div className="flex items-center justify-center py-8"><Spinner /></div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <CreditGoalSettingView
      // Re-seed the draft when the STORED goal changes (saved in another tab, or from the
      // analytics table) and only then: an unchanged goal keeps the key, so a refetch never
      // overwrites what is being typed.
      key={String(data.goal)}
      goal={data.goal}
      isSaving={setGoal.isPending}
      onSave={(goal) => setGoal.mutate(goal)}
    />
  );
}
